import { UserInfoResponse, getSiteSuccessType } from "../../../APIDocs/apiTypes";
import { Logger } from "../Logger";
import { Model } from "../Model";
import { useModel } from "./useModel";
import { useSynchronousState } from "./useSynchronousState";
import { useIsEnv } from "./useIsEnv";
import { EnvironmentVariables } from "../EnvironmentVariables";
import { getSupplierIdForHeader } from "./useJbpQuery";
import { Headers } from "cross-fetch";
import { Observable, ObservableEvents } from "../Observable";
import { useStoreUserPermissions } from "./useUserPermissions";
import { useEffect } from "react";
import { useNav, useNavToRoot, useWasNavigated } from "./useNav";
import { useNavigate } from "react-router-dom";

interface selectedContract {
    contractId: string | undefined;
    contractCustomerName: string | undefined;
    contractDisplayName: string | undefined;
}

const setSiteContactDetails = (data: getSiteSuccessType, model: Model) => {
    if(data.SiteContactDetails?.Title != undefined) {
        model.Set('viewModel.fetchedData.siteContactDetails.title', data.SiteContactDetails?.Title);
    }
    if(data.SiteContactDetails?.ContactFirstName != undefined) {
        model.Set('viewModel.fetchedData.siteContactDetails.firstname', data.SiteContactDetails?.ContactFirstName);
    }
    if(data.SiteContactDetails?.ContactSurname != undefined) {
        model.Set('viewModel.fetchedData.siteContactDetails.surname', data.SiteContactDetails?.ContactSurname);
    }
    if(data.SiteContactDetails?.EmailAddress != undefined) {
        model.Set('viewModel.fetchedData.siteContactDetails.email', data.SiteContactDetails?.EmailAddress);
    }
    if(data.SiteContactDetails?.ContactNumber != undefined) {
        model.Set('viewModel.fetchedData.siteContactDetails.number', data.SiteContactDetails?.ContactNumber);
    }
}

const setSiteDetails = (data: getSiteSuccessType, model: Model) => {
    if(data.SiteAddress.AddressName != undefined) {
        model.Set("viewModel.fetchedData.siteAddress.siteName", data.SiteAddress.AddressName);
    }
    if(data.SiteAddress.AddressLine1 != undefined) {
        model.Set("viewModel.fetchedData.siteAddress.line1", data.SiteAddress.AddressLine1);
    }
    if(data.SiteAddress.AddressLine2 != undefined) {
        model.Set("viewModel.fetchedData.siteAddress.line2", data.SiteAddress.AddressLine2);
    }
    if(data.SiteAddress.AddressLine3 != undefined) {
        model.Set("viewModel.fetchedData.siteAddress.line3", data.SiteAddress.AddressLine3);
    }
    if(data.SiteAddress.AddressLine4 != undefined) {
        model.Set("viewModel.fetchedData.siteAddress.line4", data.SiteAddress.AddressLine4);
    }
    if(data.SiteAddress.AddressLine5 != undefined) {
        model.Set("viewModel.fetchedData.siteAddress.line5", data.SiteAddress.AddressLine5);
    }
    if(data.SiteAddress.AddressLine6 != undefined) {
        model.Set("viewModel.fetchedData.siteAddress.line6", data.SiteAddress.AddressLine6);
    }
    if(data.SiteAddress.Town != undefined) {
        model.Set("viewModel.fetchedData.siteAddress.town", data.SiteAddress.Town);
    }
    if(data.SiteAddress.County != undefined) {
        model.Set("viewModel.fetchedData.siteAddress.county", data.SiteAddress.County);
    }
    if(data.SiteAddress.PostCode != undefined) {
        model.Set("viewModel.fetchedData.siteAddress.postCode", data.SiteAddress.PostCode);
    }
}

const updateUserInfoDetails = (userInfoResponseData: UserInfoResponse | undefined) => {
    Model.GetModel().Set("viewModel.fetchedData.UserTypeForCurrentUser", userInfoResponseData?.userType);
    Model.GetModel().Set("viewModel.fetchedData.ValidMPIDSForCurrentUser", userInfoResponseData?.mpid);
    Model.GetModel().Set("viewModel.fetchedData.UserRoleForCurrentUser", userInfoResponseData?.role);
    Model.GetModel().Set("viewModel.fetchedData.UserPermissionsForCurrentUser", JSON.stringify(userInfoResponseData?.permissions));
    Observable.GetObservable().emit(ObservableEvents.UserInfoLoaded, userInfoResponseData?.user);
    useStoreUserPermissions(userInfoResponseData?.permissions);
}

const updateSiteDetails = (data: getSiteSuccessType, model: Model, serviceLine: string, errorReportingFunction: ((value: boolean) => void) | undefined) => {
    setSiteDetails(data, model);

    setSiteContactDetails(data, model);

    if(data.MPID != undefined) {
        model.Set('viewModel.fetchedData.MPID', data.MPID)
        sessionStorage.setItem("userMPID", data.MPID);
    }
    if(data.OfferDateRange?.StartDate != undefined) {
        model.Set('viewModel.fetchedData.offerDateRange.startDate', data.OfferDateRange?.StartDate)
    }
    if(data.OfferDateRange?.EndDate != undefined) {
        model.Set('viewModel.fetchedData.offerDateRange.endDate', data.OfferDateRange?.EndDate)
    }
    if(data.Bookable != undefined) {
        model.Set('viewModel.fetchedData.bookable', data.Bookable)
    }

    if(data.SiteTypeCode != undefined || serviceLine == "DNO" || serviceLine == "BNO") {
        if(serviceLine == "DNO" || serviceLine == "BNO") {
            //nothing to do here. just accounting for DNO BNO
            //values we need set are done in updateRemainingModelDetails
        } else {
            model.Set('viewModel.fetchedData.siteData.siteType', data.SiteTypeCode);
            model.Set("viewModel.isWCJob", data.SiteTypeCode.indexOf("WC") != -1);
        }
    }
    else {
        Logger.Error(`failed to load site details -> Site Type Code is missing for MPAN`);
        if(errorReportingFunction != undefined) errorReportingFunction(true);
    }
}

const updateRemainingModelDetails = (contractType: string, mpan: string, serviceLine: string, model: Model) => {
    model.Set("model.selectedContractType", contractType);
    model.Set("model.MPAN", mpan);
    model.Set("model.selectedServiceLine", serviceLine)
    model.Set("model.siteLoaded", true);

    if(serviceLine == "DNO" || serviceLine == "BNO") {
        model.Set("model.results.updatedSiteType", serviceLine);
        model.Set('viewModel.fetchedData.siteData.siteType', serviceLine);
        model.Set("viewModel.isWCJob", false);
    }
}

const determineContractType = (contractType: string) => {
    switch(contractType) {
        case "HH":
            return "8";
        case "NHH":
            return "2";
        case "SUBMETER":
            return "4";
        case "DNO":
            return "100";
        case "BNO":
            return "101";
        case "MSS":
            return "111";
        case "MSA":
            return "112";
    }
    return "";
}

export const useStoreSite = () => {
    let model = useModel();
    const nav = useNavigate();
    const wasNavigated = useWasNavigated();
    useEffect(() => {
        if(wasNavigated === false) {
            //log url that was being attempted
            if(window.location.pathname != null && window.location.pathname !== "/") {
                sessionStorage.setItem("lastAttemptedUrl", window.location.pathname);
            }
            console.log("trying to access site page directly, redirecting to search component");
            // User accessed page directly, or refreshed, not via navigation. Redirect to search
            useNavToRoot(nav);
        }
    }, []);

    const dataStored = useSynchronousState(model.Get("model.siteLoaded") === true);

    const isProd = useIsEnv().isProd;
    const isAutomation = useIsEnv().isAutomation;
    const isRunningLocally = useIsEnv().isLocalHost;
    const isSitePage = window.location.pathname.startsWith('/site') === true;

    const split = window.location.pathname.lastIndexOf('/')
    const contractType = window.location.pathname.substring(split + 1, window.location.pathname.length);
    const contractTypeId = determineContractType(contractType);
    const contractData: selectedContract = {
        contractId: model.Get("model.contractId"),
        contractCustomerName: model.Get("model.contractCustomerName"),
        contractDisplayName: model.Get("model.contractDisplayName")
    }
    return {
        storeSite: async (mpan: string, serviceLine: string, data: getSiteSuccessType, setDataErrorOccurred?: (value: boolean) => void) => {
            if(isProd === true && isSitePage === false && dataStored.get() === false) {
                useNav('/404', nav);
                return;
            }

            //returned to/on sitePage, some changes may have happened off the back of job booking.
            //reconstruct the model with new data that has been received from the get site call.
            //DO NOT MOVE - NEEDS TO SIT HIGH ENOUGH IN LOGIC TO ENSURE MODEL IS CLEARED BEFORE MORE BOOKINGS ARE POSSIBLY MADE.
            if(isSitePage) {
                model.teardownModel();
                model = Model.GetModel();
                dataStored.set(false);
            }

            if(dataStored.get() == true) {
                return;
            }

            //because this returns an async promise, the rest of the app tries to render before data is re-added to the model.
            //This includes api requests that require the model to be populated with data.
            //re-add the contract data here if it exists to prevent race conditions.
            //Ideally, the jbp api requests should not fire until the data is ready.
            if(contractData?.contractId !== undefined) {
                model.Set("model.contractId", contractData?.contractId);
                model.Set("model.contractCustomerName", contractData?.contractCustomerName);
                model.Set("model.contractDisplayName", contractData?.contractDisplayName);
            }
            model.Set("model.wasNavigated", wasNavigated);

            //re-validate user credentials everytime to ensure we always have an accurate user role/type
            return new Promise<void>((res, rej) => {

                const determineIfMpanShouldBeSent = () => {
                    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                    if(isAutomation || isRunningLocally) {
                        return mpan;
                    }

                    return undefined;
                }

                const path = `${EnvironmentVariables.ApiUrl}/jbp/userInfo`
                const MPID = getSupplierIdForHeader("")
                const mpanToUseInQuery: string | undefined = determineIfMpanShouldBeSent();

                console.log(`Querying ${path}`)

                const constructedHeader = new Headers({
                    'Contract-Type': contractType,
                    'MPID': MPID,
                });


                //this is only being used by automation/local running to test out userType/role functionality based on explicitly set MPAN's that return specific user types/roles.
                if(mpanToUseInQuery != null) {
                    constructedHeader.append("MPAN", mpanToUseInQuery);
                }

                fetch(path, {
                    headers: constructedHeader,
                    credentials: 'include'
                })
                    .then((responseData) => responseData.json())
                    .then((userInfoResponseData: UserInfoResponse) => {
                        updateUserInfoDetails(userInfoResponseData);
                        if(Object.keys(data).length > 0) {
                            updateSiteDetails(data, model, serviceLine, setDataErrorOccurred);
                        } else if(!["DNO", "BNO", "SUBMETER"].includes(serviceLine)) {
                            Logger.Error(`failed to load site details -> No data found for MPAN = ${mpan}`);
                            if(setDataErrorOccurred != undefined) setDataErrorOccurred(true);
                            rej("MISSING SITE DATA");
                        }
                        updateRemainingModelDetails(contractTypeId, mpan, serviceLine, model);
                        dataStored.set(true);
                        res();
                    })
                    .catch(e => {
                        console.log("Cannot determine user credentials navigating to search component: " + e);
                        useNavToRoot(nav);
                        return;
                    });
            })

        },
        dataStored: dataStored.get()
    }
}
